// "Routines" are marketed as "Agents" in the UI; the code name remains "routines".
import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Divider, Group, Stack, Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Panel, PanelGroup, PanelResizeHandle} from 'react-resizable-panels';

import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import PreviewBadge from 'ui-library/badges/PreviewBadge';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredSpinner} from 'ui-library/Spinner';
import {getUserMessage} from 'util/Net';

import RoutineCard from './RoutineCard';
import RoutineDetailPanel, {DetailPanelMode} from './RoutineDetailPanel';
import {RoutineExample} from './routineExamples';
import RoutineExamplesSection from './RoutineExamplesSection';
import {runRoutine as runRoutineApi} from './RoutinesDAO';
import {useRoutineSectionHeaderStyles} from './routineStyles';
import {Routine, RoutineStatus, TriggerType} from './RoutineTypes';
import useRoutines from './useRoutines';

interface RoutinesPageProps {
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
  isExpanded: boolean;
}

const useStyles = createStyles((theme) => ({
  splitContainer: {
    display: 'flex',
    overflow: 'hidden',
    margin: '-16px -24px',
    height: 'calc(100vh - 60px)',
  },
  listStack: {
    flex: '1 1 auto',
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
    maxWidth: 540,
  },
  listStackCentered: {
    maxWidth: 540,
    margin: '0 auto',
  },
  listPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
    padding: '16px 24px',
  },
  detailPanel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    borderLeft: `1px solid ${theme.colors.gray[3]}`,
  },
  panelGroup: {
    width: '100%',
    height: '100%',
  },
  resizer: {
    width: '6px',
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    flexShrink: 0,
    ':hover': {
      backgroundColor: theme.colors.gray[2],
    },
  },
}));

export default function RoutinesPage({
  timeBasedEnabled,
  eventBasedEnabled,
  isExpanded,
}: RoutinesPageProps): JSX.Element {
  const {routines, isLoading, isError, refetch} = useRoutines();
  const [panelMode, setPanelMode] = useState<DetailPanelMode | null>(null);
  const previousPanelMode = useRef<DetailPanelMode | null>(null);
  const previousIsExpanded = useRef(isExpanded);
  const {classes} = useStyles();
  const {classes: sectionHeaderClasses} = useRoutineSectionHeaderStyles();

  useEffect(() => {
    const panelJustOpened = previousPanelMode.current == null && panelMode != null;
    if (panelJustOpened && !isExpanded) {
      SidepanelActions.expand();
    }
    previousPanelMode.current = panelMode;
  }, [panelMode, isExpanded]);

  useEffect(() => {
    const wasExpanded = previousIsExpanded.current;
    if (wasExpanded && !isExpanded && panelMode != null) {
      setPanelMode(null);
    }
    previousIsExpanded.current = isExpanded;
  }, [isExpanded, panelMode]);

  const handleClose = useCallback(() => {
    setPanelMode(null);
  }, []);

  const handleMutationSuccess = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleCardClick = useCallback((routine: Routine) => {
    setPanelMode({type: 'edit', routineId: routine.id});
  }, []);

  // Run a saved routine immediately and, on success, surface a toast linking to the new chat.
  // The run goes through the same execution path a scheduled firing uses, so the chat appears in
  // the normal chat list. A failure surfaces the server's message as an error toast.
  const handleRunRoutine = useCallback(async (routine: Routine) => {
    try {
      const {chatSessionId} = await runRoutineApi(routine.id);
      notifications.success('Your Agent is running.', {
        actionText: 'View chat',
        onActionClick: () => {
          openChat(chatSessionId);
        },
      });
    } catch (e) {
      const message = getUserMessage(e);
      // Re-throw unexpected errors (those without a user-facing message) rather than swallowing
      // them, matching the useRoutineMutations error handling. Known server errors surface as a
      // toast.
      if (message == null) {
        throw e;
      }
      notifications.warn(message);
    }
  }, []);

  const handleNewRoutine = useCallback(() => {
    setPanelMode({type: 'create'});
  }, []);

  const handleSelectExample = useCallback((example: RoutineExample) => {
    setPanelMode({type: 'create', example: example.formState, exampleId: example.id});
  }, []);

  const enabledTriggerTypes = useMemo(() => {
    const types = new Set<TriggerType>();
    if (timeBasedEnabled) {
      types.add(TriggerType.TIME_BASED);
    }
    if (eventBasedEnabled) {
      types.add(TriggerType.EVENT_BASED);
    }
    return types;
  }, [timeBasedEnabled, eventBasedEnabled]);

  const visibleRoutines = useMemo(
    () => routines.filter((s) => enabledTriggerTypes.has(s.triggerType)),
    [routines, enabledTriggerTypes]
  );

  const activeRoutines = useMemo(
    () =>
      visibleRoutines.filter(
        (s) => s.status === RoutineStatus.ACTIVE || s.status === RoutineStatus.PENDING_REGISTRATION
      ),
    [visibleRoutines]
  );
  const pausedRoutines = useMemo(
    () => visibleRoutines.filter((s) => s.status === RoutineStatus.PAUSED),
    [visibleRoutines]
  );

  const listContent = (
    <Stack spacing="lg" className={classes.listStack}>
      <Stack spacing="sm">
        <Group noWrap={true} position="apart" align="center">
          <Group noWrap={true} spacing="xs" align="center">
            <Text fw="bold" size="xl">
              Agents
            </Text>
            <PreviewBadge />
          </Group>
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            icon="plus"
            size={ButtonSize.SMALL}
            text="Add"
            onClick={handleNewRoutine}
          />
        </Group>
        <Text fw="normal" size="sm" c="dimmed">
          Set up Agents to run automatically at a set time or triggered by an event.
        </Text>
      </Stack>

      <RoutineExamplesSection
        timeBasedEnabled={timeBasedEnabled}
        eventBasedEnabled={eventBasedEnabled}
        onSelectExample={handleSelectExample}
      />

      {isLoading ? (
        <CenteredSpinner />
      ) : isError ? (
        <Text size="sm" c="dimmed">
          Failed to load Agents. Please try again.
        </Text>
      ) : (
        <Stack spacing="xl">
          <Stack spacing="sm">
            <Text className={sectionHeaderClasses.sectionHeader}>Active agents</Text>
            {activeRoutines.map((routine) => (
              <RoutineCard
                key={routine.id}
                routine={routine}
                onClick={handleCardClick}
                onRun={handleRunRoutine}
              />
            ))}
          </Stack>

          {pausedRoutines.length > 0 && (
            <>
              <Divider />
              <Stack spacing="sm">
                <Text className={sectionHeaderClasses.sectionHeader}>Paused agents</Text>
                {pausedRoutines.map((routine) => (
                  <RoutineCard
                    key={routine.id}
                    routine={routine}
                    onClick={handleCardClick}
                    onRun={handleRunRoutine}
                  />
                ))}
              </Stack>
            </>
          )}
        </Stack>
      )}
    </Stack>
  );

  if (panelMode == null) {
    return <div className={classes.listStackCentered}>{listContent}</div>;
  }

  return (
    <Box
      className={classes.splitContainer}
      {...getTestProps({component: 'RoutinesPage', element: 'splitView'})}
    >
      <PanelGroup direction="horizontal" className={classes.panelGroup}>
        <Panel defaultSize={40} minSize={25} maxSize={70}>
          <Box className={classes.listPanel}>{listContent}</Box>
        </Panel>
        <PanelResizeHandle className={classes.resizer} />
        <Panel defaultSize={60} minSize={30} maxSize={75}>
          <Box className={classes.detailPanel}>
            <RoutineDetailPanel
              key={
                panelMode.type === 'create'
                  ? `create-${panelMode.exampleId ?? 'blank'}`
                  : `edit-${panelMode.routineId}`
              }
              mode={panelMode}
              onClose={handleClose}
              onMutationSuccess={handleMutationSuccess}
              timeBasedEnabled={timeBasedEnabled}
              eventBasedEnabled={eventBasedEnabled}
            />
          </Box>
        </Panel>
      </PanelGroup>
    </Box>
  );
}
